import { createContext, useContext, useState, useRef, ReactNode, useCallback, useMemo, useEffect } from 'react';
import type { SystemRecInput, SystemRecOutput, DecisionScenario } from '@core/types';
import type { NormalizedOutput } from '@core/normalizeOutput';
import type { CostOverTimeChartData, AnnualCostChartData, CarbonSavingsChartData } from '@core/chartData';
import { saveStorySnapshot } from '../utils/storySnapshot';
import { stableStringify } from '../utils/stableStringify';
import {
  updateRawInputSnapshot,
  detectDroppedFields,
  getDiagnosticsState,
  questionMappings,
  trackLifecycleEvent,
  convertToSystemRecInput,
} from '../mapping';

// Cache the radiatorSize mapping for performance
const RADIATOR_SIZE_MAPPING = questionMappings.find(m => m.questionId === 'radiatorSize');

export interface CollectedData {
  // Section confirmation statuses (persisted across navigation)
  sectionStatuses?: {
    home: 'estimated' | 'confirmed';
    occupancy: 'estimated' | 'confirmed';
    water: 'estimated' | 'confirmed';
    energy: 'estimated' | 'confirmed';
    plans: 'estimated' | 'confirmed';
    retrofit: 'estimated' | 'confirmed';
    experience: 'estimated' | 'confirmed';
  };

  // Home
  propertyType: string;
  propertyAgeBand: string;
  bedrooms: string;
  bathrooms: string;
  availableSpace: string;
  outdoorSpace: string;

  // Occupancy
  occupancyCount: string;
  occupancyTiming: string;  // When home is typically occupied
  temporaryGuests: string;
  futureDemand: string;

  // Water
  hotWaterDemand: string;
  usagePattern: string;
  usageFrequency: string;

  // Energy
  boilerType: string;
  waterSystem: string;
  electricalCapacity: string;
  mainsPressure: string;
  flowRate: string;
  drawsPerDay: string;

  mainsFlowLpm?: string;
  mainsStaticPressureBar?: string;
  mainsDynamicFlowLpm?: string;
  mainsDynamicPressureBar?: string;
  coldMainType?: string;

  // Thermostat preferences (for gas boiler systems)
  includeThermostat?: string;           // 'yes', 'no'
  currentThermostatType?: string;       // 'mechanical', 'basic_digital', 'smart_on_off', 'smart_load_comp'
  thermostatBudget?: string;            // 'basic', 'mid', 'premium'
  wantLoadCompensation?: string;        // 'yes', 'no', 'unsure'
  wantSmartStart?: string;              // 'yes', 'no', 'unsure'
  wantMultiZone?: string;               // 'yes', 'no'

  // Baseline "Cost of Doing Nothing" inputs
  systemAge: string;
  efficiencySource: string;  // 'sedbuk', 'epc'/'erp', 'unknown'
  sedbukRating: string;      // '90', '85', etc. or 'unknown' (DEPRECATED - use sedbukBand)
  sedbukBand: string;        // 'A', 'B', 'C', 'D', 'E', 'F', 'G' or 'unknown'
  gcNumber: string;          // GC number (e.g., '47-116-30') or 'unknown'
  boilerMake: string;        // Manufacturer (e.g., 'Worcester Bosch') or 'unknown'
  boilerModel: string;       // Model (e.g., 'Greenstar 4000 25kW') or 'unknown'
  currentBoilerKw?: string;  // Current boiler rated output (kW)
  epcBand: string;           // 'A', 'B', 'C', etc. or 'unknown'
  currentFuelType: string;   // 'gas', 'oil', 'electric', 'lpg'
  sludgeRisk: string;        // 'none', 'possible', 'likely', 'confirmed'

  // Emitter profile (for heat pump suitability)
  emitterType?: string;             // 'radiators', 'underfloor', 'mixed', 'unknown'
  radiatorSize?: string;            // 'small', 'typical', 'oversized', 'unknown'
  ufhExtent?: string;               // 'none', 'partial', 'full'
  emitterLowTempSuitability?: string; // 'unknown', 'likely_ok', 'likely_needs_upgrade', 'definitely_needs_upgrade'
  emitterRoomsStruggle?: string;     // 'yes', 'no', 'unknown'
  pipeBore?: string;                // '8mm', '10mm', '15mm', '22mm', 'unknown'

  // Simplified insulation level (new simplified field)
  insulationLevel?: string;          // 'good', 'average', 'poor', 'unknown'

  // Retrofit insulation profile
  retrofitLoftInsulation?: string;    // 'unknown', 'none', 'partial', 'modern'
  retrofitWallInsulation?: string;    // 'unknown', 'none', 'cavity', 'solid_insulated', 'solid_uninsulated'
  retrofitWindows?: string;           // 'unknown', 'single', 'double', 'modern'
  retrofitDraughtProofing?: string;   // 'unknown', 'none', 'partial', 'modern'

  // Contamination symptoms (optional, for advanced assessment)
  contaminationAssessment?: string; // 'skip', 'assess'
  radiatorColdBottom?: string;      // 'yes', 'no'
  kettlingWhistling?: string;       // 'yes', 'no'
  gurglingAir?: string;             // 'yes', 'no'
  pumpNoise?: string;               // 'yes', 'no'
  frequentBleeding?: string;        // 'yes', 'no'
  slowWarmup?: string;              // 'yes', 'no'
  visibleMagnetite?: string;        // 'yes', 'no'
  systemType?: string;              // 'sealed', 'open_vented', 'unknown'
  hasMagneticFilter?: string;       // 'yes', 'no', 'unknown'
  hardWaterArea?: string;           // 'yes', 'no', 'unknown'

  // Plans
  lengthOfStay: string;
  lifeStage: string;
  budgetPriority: string;
  disruptionTolerance: string;
  urgency: string;

  // Upgrade priorities (multi-dimensional priority ranking)
  upgradePriorities?: Array<{ key: string; rank: number }>;

  // Future plans (used by fit scoring)
  futurePlans?: Array<{ key: string; horizon?: string }>;

  // System type preferences - which combinations to consider
  considerCombi?: string;           // 'yes', 'no'
  considerSystem?: string;          // 'yes', 'no'
  considerRegular?: string;         // 'yes', 'no'
  considerHeatPump?: string;        // 'yes', 'no' (ASHP)
  considerGSHP?: string;            // 'yes', 'no' (Ground Source HP)
  considerHybrid?: string;          // 'yes', 'no'
  considerStorageCombi?: string;    // 'yes', 'no'
  considerVentedCylinder?: string;  // 'yes', 'no'
  considerMixergyVented?: string;   // 'yes', 'no'
  considerUnventedCylinder?: string; // 'yes', 'no'
  considerMixergyUnvented?: string; // 'yes', 'no'
  considerOnDemand?: string;        // 'yes', 'no'
  selectedCompareOptions?: Array<{ boiler: string; water: string }>;

  // LCOO (Whole Life Cost) inputs
  enableLcoo?: string;              // 'yes', 'no'
  lcooPeriodYears?: string;         // '20', '25', '30'
  lcooGasTariff?: string;           // pence per kWh
  lcooElecTariff?: string;          // pence per kWh

  // Per-option LCOO data (for options A, B, C, D, E)
  lcooOptionA_upfront?: string;
  lcooOptionA_beforeGrants?: string;  // 'yes', 'no'
  lcooOptionA_applyBUS?: string;      // 'yes', 'no'
  lcooOptionA_maintenance?: string;
  lcooOptionA_retainCylinder?: string; // 'yes', 'no'
  lcooOptionA_cylinderAge?: string;
  lcooOptionA_cylinderCost?: string;

  lcooOptionB_upfront?: string;
  lcooOptionB_beforeGrants?: string;
  lcooOptionB_applyBUS?: string;
  lcooOptionB_maintenance?: string;
  lcooOptionB_retainCylinder?: string;
  lcooOptionB_cylinderAge?: string;
  lcooOptionB_cylinderCost?: string;

  lcooOptionC_upfront?: string;
  lcooOptionC_beforeGrants?: string;
  lcooOptionC_applyBUS?: string;
  lcooOptionC_maintenance?: string;
  lcooOptionC_retainCylinder?: string;
  lcooOptionC_cylinderAge?: string;
  lcooOptionC_cylinderCost?: string;

  // Experience questions (for post-engine experience fit layer)
  simultaneousHotWaterUse?: string;       // 'never', 'occasionally', 'most_days'
  heatingContinuityImportance?: string;   // 'not_important', 'important', 'very_important'
  airingCupboardUse?: string;             // 'no', 'occasionally', 'yes_regularly'
  spacePressure?: string;                 // 'very_tight', 'some_flexibility', 'plenty'
  operatingStylePreference?: string;      // 'quick_response', 'balanced', 'steady_consistent'
  noiseSensitivity?: string;              // 'not_bothered', 'somewhat', 'very_sensitive'
  environmentalIntent?: string;           // 'nice_bonus', 'ready_for_later', 'greenest_now'
  solarIntent?: string;                   // 'none', 'planned', 'installed', 'unknown'

  // Customer story education selections
  storyEducationSelections?: string[];

  // Reporting and reference fields
  customerReference?: string;
  reportDate?: string;
  documentReference?: string;
}

export interface RecommendationResult {
  output: SystemRecOutput;
  normalizedOutput?: NormalizedOutput;
  scenarios: DecisionScenario[];  // NEW: Unified scenario list
  costOverTimeData: CostOverTimeChartData | null;
  annualCostData: AnnualCostChartData | null;
  carbonSavingsData: CarbonSavingsChartData | null;
  /** The normalized input that was ACTUALLY passed to computeSystemRecommendation.
   *  This ensures debug bundles show the exact input used, avoiding divergence. */
  normalizedInput?: SystemRecInput;
}

export interface ConfidenceScore {
  level: 'low' | 'medium' | 'high';
  percentage: number;
  answeredCount: number;
  totalCount: number;
}

/**
 * Section completion status derived from actual data fields.
 * This replaces the stale sectionStatuses flag that only updates on modal save.
 */
export type DerivedSectionStatuses = {
  home: 'estimated' | 'confirmed';
  occupancy: 'estimated' | 'confirmed';
  water: 'estimated' | 'confirmed';
  energy: 'estimated' | 'confirmed';
  plans: 'estimated' | 'confirmed';
  retrofit: 'estimated' | 'confirmed';
  experience: 'estimated' | 'confirmed';
};

/**
 * Section completion percentages based on filled fields.
 * Returns the percentage of required/optional fields that are filled.
 */
export type SectionCompletionPercentages = {
  home: number;
  occupancy: number;
  water: number;
  energy: number;
  plans: number;
  retrofit: number;
  experience: number;
};

/**
 * Derive section completion from actual field values.
 * This ensures the UI shows correct completion even if data was entered
 * outside the modal flow (e.g., on dedicated screens like Screen1dEmittersRetrofit).
 */
export function deriveSectionCompletion(data: CollectedData | null): DerivedSectionStatuses {
  if (!data) {
    return {
      home: 'estimated',
      occupancy: 'estimated',
      water: 'estimated',
      energy: 'estimated',
      plans: 'estimated',
      retrofit: 'estimated',
      experience: 'estimated',
    };
  }

  // Helper to check if a value is meaningfully filled in
  const isFilled = (value: string | undefined | null): boolean => {
    return !!value && value !== 'unknown' && value !== 'not_sure' && value !== '';
  };

  // Home: complete if property type, age, bedrooms, and bathrooms are filled
  const homeComplete = isFilled(data.propertyType) &&
                       isFilled(data.propertyAgeBand) &&
                       isFilled(data.bedrooms) &&
                       isFilled(data.bathrooms);

  // Occupancy: complete if occupancy count AND timing are filled
  // Operating style preference is also a good signal
  const occupancyComplete = isFilled(data.occupancyCount) &&
                            (isFilled(data.occupancyTiming) || isFilled(data.operatingStylePreference));

  // Water: complete if hot water demand is filled
  const waterComplete = isFilled(data.hotWaterDemand);

  // Energy: complete if boiler type and water system are filled
  const energyComplete = isFilled(data.boilerType) && isFilled(data.waterSystem);

  // Plans: complete if length of stay and urgency are filled
  const plansComplete = isFilled(data.lengthOfStay) && isFilled(data.urgency);

  // Retrofit/Emitters: complete if ANY insulation or emitter field is meaningfully filled
  // (not all unknown/null)
  const hasRetrofitData = isFilled(data.insulationLevel) ||
                          isFilled(data.retrofitLoftInsulation) ||
                          isFilled(data.retrofitWallInsulation) ||
                          isFilled(data.retrofitWindows) ||
                          isFilled(data.retrofitDraughtProofing);
  const hasEmitterData = isFilled(data.emitterType) ||
                         isFilled(data.radiatorSize) ||
                         isFilled(data.ufhExtent);
  const retrofitComplete = hasRetrofitData || hasEmitterData;

  // Experience: complete if ANY experience field is meaningfully filled
  const hasExperienceData = isFilled(data.simultaneousHotWaterUse) ||
                           isFilled(data.heatingContinuityImportance) ||
                           isFilled(data.airingCupboardUse) ||
                           isFilled(data.spacePressure) ||
                           isFilled(data.operatingStylePreference) ||
                           isFilled(data.noiseSensitivity) ||
                           isFilled(data.environmentalIntent) ||
                           isFilled(data.solarIntent);
  const experienceComplete = hasExperienceData;

  return {
    home: homeComplete ? 'confirmed' : 'estimated',
    occupancy: occupancyComplete ? 'confirmed' : 'estimated',
    water: waterComplete ? 'confirmed' : 'estimated',
    energy: energyComplete ? 'confirmed' : 'estimated',
    plans: plansComplete ? 'confirmed' : 'estimated',
    retrofit: retrofitComplete ? 'confirmed' : 'estimated',
    experience: experienceComplete ? 'confirmed' : 'estimated',
  };
}

/**
 * Calculate completion percentage for each section.
 * Returns percentage of fields filled (0-100).
 */
export function calculateSectionPercentages(data: CollectedData | null): SectionCompletionPercentages {
  if (!data) {
    return {
      home: 0,
      occupancy: 0,
      water: 0,
      energy: 0,
      plans: 0,
      retrofit: 0,
      experience: 0,
    };
  }

  // Helper to check if a value is meaningfully filled in
  const isFilled = (value: string | undefined | null): boolean => {
    return !!value && value !== 'unknown' && value !== 'not_sure' && value !== '';
  };

  // HOME: 4 core fields (propertyType, propertyAgeBand, bedrooms, bathrooms)
  const homeFields = [data.propertyType, data.propertyAgeBand, data.bedrooms, data.bathrooms];
  const homeFilledCount = homeFields.filter(isFilled).length;
  const homePercentage = Math.round((homeFilledCount / homeFields.length) * 100);

  // OCCUPANCY: 2 core fields (occupancyCount, occupancyTiming)
  const occupancyFields = [data.occupancyCount, data.occupancyTiming];
  const occupancyFilledCount = occupancyFields.filter(isFilled).length;
  const occupancyPercentage = Math.round((occupancyFilledCount / occupancyFields.length) * 100);

  // WATER: 1 core field (hotWaterDemand)
  const waterFields = [data.hotWaterDemand];
  const waterFilledCount = waterFields.filter(isFilled).length;
  const waterPercentage = Math.round((waterFilledCount / waterFields.length) * 100);

  // ENERGY: 2 core fields (boilerType, waterSystem)
  const energyFields = [data.boilerType, data.waterSystem];
  const energyFilledCount = energyFields.filter(isFilled).length;
  const energyPercentage = Math.round((energyFilledCount / energyFields.length) * 100);

  // PLANS: 2 core fields (lengthOfStay, urgency)
  const plansFields = [data.lengthOfStay, data.urgency];
  const plansFilledCount = plansFields.filter(isFilled).length;
  const plansPercentage = Math.round((plansFilledCount / plansFields.length) * 100);

  // RETROFIT: Count any filled insulation or emitter fields
  const retrofitFields = [
    data.insulationLevel,
    data.retrofitLoftInsulation,
    data.retrofitWallInsulation,
    data.retrofitWindows,
    data.retrofitDraughtProofing,
    data.emitterType,
    data.radiatorSize,
    data.ufhExtent,
  ];
  const retrofitFilledCount = retrofitFields.filter(isFilled).length;
  const retrofitPercentage = Math.round((retrofitFilledCount / retrofitFields.length) * 100);

  // EXPERIENCE: Count any filled experience fields
  const experienceFields = [
    data.simultaneousHotWaterUse,
    data.heatingContinuityImportance,
    data.airingCupboardUse,
    data.spacePressure,
    data.operatingStylePreference,
    data.noiseSensitivity,
    data.environmentalIntent,
    data.solarIntent,
  ];
  const experienceFilledCount = experienceFields.filter(isFilled).length;
  const experiencePercentage = Math.round((experienceFilledCount / experienceFields.length) * 100);

  return {
    home: homePercentage,
    occupancy: occupancyPercentage,
    water: waterPercentage,
    energy: energyPercentage,
    plans: plansPercentage,
    retrofit: retrofitPercentage,
    experience: experiencePercentage,
  };
}

interface RecommendationContextType {
  collectedData: CollectedData | null;
  setCollectedData: (data: CollectedData | null | ((prev: CollectedData | null) => CollectedData | null)) => void;
  recommendationResult: RecommendationResult | null;
  setRecommendationResult: (result: RecommendationResult | null) => void;
  selectedOptionId: string | null;
  setSelectedOptionId: (id: string | null) => void;
  activeDocumentId: string | null;
  setActiveDocumentId: (id: string | null) => void;
  convertToSystemRecInput: (data: CollectedData) => SystemRecInput;
  calculateConfidence: (data: CollectedData | null) => ConfidenceScore;
  derivedSectionStatuses: DerivedSectionStatuses;
  sectionCompletionPercentages: SectionCompletionPercentages;
}

const RecommendationContext = createContext<RecommendationContextType | undefined>(undefined);

export const useRecommendation = () => {
  const context = useContext(RecommendationContext);
  if (!context) {
    throw new Error('useRecommendation must be used within RecommendationProvider');
  }
  return context;
};

interface RecommendationProviderProps {
  children: ReactNode;
}

export const RecommendationProvider = ({ children }: RecommendationProviderProps) => {
  // Track store creation
  const isInitialMount = useRef(true);
  
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      trackLifecycleEvent('store_created', 'RecommendationContext:RecommendationProvider', {
        note: 'RecommendationContext initialized',
      });
    }
  }, []);

  // Load from sessionStorage on init to persist across page navigations
  const [collectedData, setCollectedData] = useState<CollectedData | null>(() => {
    const saved = sessionStorage.getItem('collectedData');
    if (!saved || saved === 'undefined' || saved === 'null') return null;
    try {
      return JSON.parse(saved);
    } catch {
      return null;
    }
  });
  const [recommendationResult, setRecommendationResult] = useState<RecommendationResult | null>(() => {
    const saved = sessionStorage.getItem('recommendationResult');
    if (!saved || saved === 'undefined' || saved === 'null') return null;
    try {
      return JSON.parse(saved);
    } catch {
      return null;
    }
  });
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(() => {
    const saved = sessionStorage.getItem('selectedOptionId');
    if (!saved || saved === 'undefined' || saved === 'null') return null;
    return saved;
  });
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(() => {
    const saved = sessionStorage.getItem('activeDocumentId');
    if (!saved || saved === 'undefined' || saved === 'null') return null;
    return saved;
  });

  // Track previous data for dropped field detection
  const prevDataRef = useRef<CollectedData | null>(null);
  const prevNormalizedRef = useRef<SystemRecInput | null>(null);

  // Save to sessionStorage whenever state changes
  const setCollectedDataPersisted = useCallback((data: CollectedData | null | ((prev: CollectedData | null) => CollectedData | null)) => {
    setCollectedData((prev) => {
      const newData = typeof data === 'function' ? data(prev) : data;
      if (newData) {
        sessionStorage.setItem('collectedData', JSON.stringify(newData));
        // Update raw input snapshot for devtools
        if (getDiagnosticsState().enabled) {
          updateRawInputSnapshot(newData as unknown as Record<string, unknown>, { docId: activeDocumentId || undefined });
          
          // Track mapping update
          const keys = Object.keys(newData).length;
          if (!prev || keys === 0) {
            trackLifecycleEvent('mapping_initialized', 'RecommendationContext:setCollectedDataPersisted', {
              keys,
              note: 'Initial mapping data set',
            });
          } else {
            trackLifecycleEvent('mapping_updated', 'RecommendationContext:setCollectedDataPersisted', {
              keys,
              note: 'Mapping data updated',
            });
          }
        }
      } else {
        sessionStorage.removeItem('collectedData');
        // Track mapping reset
        if (getDiagnosticsState().enabled && prev) {
          trackLifecycleEvent('mapping_reset', 'RecommendationContext:setCollectedDataPersisted', {
            note: 'Mapping data cleared',
          });
        }
      }
      return newData;
    });
  }, [activeDocumentId]);

  const setRecommendationResultPersisted = useCallback((result: RecommendationResult | null) => {
    setRecommendationResult(result);
    if (result) {
      sessionStorage.setItem('recommendationResult', JSON.stringify(result));
    } else {
      sessionStorage.removeItem('recommendationResult');
    }
  }, []);

  const setSelectedOptionIdPersisted = useCallback((id: string | null) => {
    setSelectedOptionId(id);
    if (id) {
      sessionStorage.setItem('selectedOptionId', id);
    } else {
      sessionStorage.removeItem('selectedOptionId');
    }
  }, []);

  const setActiveDocumentIdPersisted = useCallback((id: string | null) => {
    setActiveDocumentId(id);
    if (id) {
      sessionStorage.setItem('activeDocumentId', id);
    } else {
      sessionStorage.removeItem('activeDocumentId');
    }
  }, []);

  const calculateConfidence = useCallback((data: CollectedData | null): ConfidenceScore => {
    // Core required fields for basic recommendation
    const coreFields = [
      'propertyType', 'propertyAgeBand', 'bedrooms', 'bathrooms',
      'occupancyCount', 'hotWaterDemand', 'boilerType', 'waterSystem',
      'lengthOfStay', 'budgetPriority', 'disruptionTolerance', 'urgency'
    ];

    // Important fields that improve confidence
    const importantFields = [
      'availableSpace', 'electricalCapacity', 'mainsPressure', 'flowRate',
      'systemAge', 'currentFuelType', 'sludgeRisk', 'futureDemand'
    ];

    // Enhanced fields for best confidence
    const enhancedFields = [
      'drawsPerDay', 'temporaryGuests', 'usagePattern',
      'usageFrequency', 'outdoorSpace'
    ];

    // Efficiency source confidence penalty
    // If efficiency data is assumed/unknown, cap confidence at medium
    let efficiencySourcePenalty = false;

    // Calculate max possible score with weighted values
    const maxScore = (coreFields.length * 3) + (importantFields.length * 2) + (enhancedFields.length * 1);

    if (!data) {
      return { level: 'low', percentage: 0, answeredCount: 0, totalCount: maxScore };
    }

    let answeredCount = 0;

    // Count core fields (weighted 3x)
    coreFields.forEach(field => {
      const value = data[field as keyof CollectedData];
      if (value && value !== 'unknown' && value !== '') {
        answeredCount += 3;
      }
    });

    // Count important fields (weighted 2x)
    importantFields.forEach(field => {
      const value = data[field as keyof CollectedData];
      if (value && value !== 'unknown' && value !== '') {
        answeredCount += 2;
      }
    });

    // Count enhanced fields (weighted 1x)
    enhancedFields.forEach(field => {
      const value = data[field as keyof CollectedData];
      if (value && value !== 'unknown' && value !== '') {
        answeredCount += 1;
      }
    });

    // Check efficiency source quality
    // HIGH confidence only if we have GC number or SEDBUK data from lookup
    // If efficiency is unknown/assumed, cap at MEDIUM even with complete form
    const efficiencySource = data.efficiencySource;
    const normalizedGcForConfidence = normalizeUnknown(data.gcNumber);
    const normalizedSedbukBandForConfidence = normalizeUnknown(data.sedbukBand);

    if (efficiencySource === 'unknown' ||
        (efficiencySource === 'sedbuk' && !normalizedGcForConfidence && !normalizedSedbukBandForConfidence)) {
      // Efficiency data is assumed - cap confidence
      efficiencySourcePenalty = true;
    }

    // Calculate percentage
    const percentage = Math.round((answeredCount / maxScore) * 100);

    // Determine confidence level
    let level: 'low' | 'medium' | 'high';
    if (percentage >= 75 && !efficiencySourcePenalty) {
      // Only HIGH if form is complete AND efficiency source is verified
      level = 'high';
    } else if (percentage >= 45) {
      level = 'medium';
    } else {
      level = 'low';
    }

    return {
      level,
      percentage,
      answeredCount,
      totalCount: maxScore
    };
  }, []);

  // Compute derived section statuses based on actual data
  const derivedSectionStatuses = useMemo(() => {
    return deriveSectionCompletion(collectedData);
  }, [collectedData]);

  // Compute section completion percentages based on actual data
  const sectionCompletionPercentages = useMemo(() => {
    return calculateSectionPercentages(collectedData);
  }, [collectedData]);

  // Save story snapshot whenever data or results change
  useEffect(() => {
    if (collectedData) {
      const engineInput = recommendationResult?.normalizedInput
        ?? convertToSystemRecInput(collectedData);
      const inputHash = stableStringify(engineInput);

      saveStorySnapshot({
        rawInput: collectedData,
        engineOutput: recommendationResult ? {
          input: engineInput,
          output: recommendationResult.output,
          normalizedOutput: recommendationResult.normalizedOutput,
          inputHash,
          generatedAt: new Date().toISOString(),
        } : null,
        selectedOptionId,
      });
    }
  }, [collectedData, recommendationResult, selectedOptionId]);

  // Detect dropped fields for devtools
  // Note: Normalization is already handled by updateRawInputSnapshot() which is called
  // in setCollectedDataPersisted (line 482) when data changes. This useEffect only needs
  // to track dropped fields by comparing prev/current state.
  useEffect(() => {
    if (!getDiagnosticsState().enabled || !collectedData) return;

    // Get the normalized input from diagnostics state (already normalized by updateRawInputSnapshot)
    const diagnosticsState = getDiagnosticsState();
    const normalizedInput = diagnosticsState.normalizedInput;

    // Detect dropped fields (raw changed but normalized didn't)
    // This requires comparing previous raw/normalized with current raw/normalized
    if (prevDataRef.current && prevNormalizedRef.current && normalizedInput) {
      detectDroppedFields(
        prevDataRef.current as unknown as Record<string, unknown>,
        collectedData as unknown as Record<string, unknown>,
        prevNormalizedRef.current as unknown as Record<string, unknown>,
        normalizedInput
      );
    }

    // Update refs for next comparison
    // Convert current collectedData to normalized form for next comparison
    try {
      const currentNormalized = convertToSystemRecInput(collectedData);
      prevDataRef.current = collectedData;
      prevNormalizedRef.current = currentNormalized;
    } catch (error) {
      // If normalization fails, log it for debugging but don't block
      // Note: The primary normalization in updateRawInputSnapshot() already tracks failures
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[RecommendationContext] Failed to convert data for ref comparison:', errorMessage);
      prevDataRef.current = collectedData;
      // Keep previous normalized ref for next comparison
    }
  }, [collectedData, activeDocumentId]);

  const contextValue = useMemo(() => ({
    collectedData,
    setCollectedData: setCollectedDataPersisted,
    recommendationResult,
    setRecommendationResult: setRecommendationResultPersisted,
    selectedOptionId,
    setSelectedOptionId: setSelectedOptionIdPersisted,
    activeDocumentId,
    setActiveDocumentId: setActiveDocumentIdPersisted,
    convertToSystemRecInput,
    calculateConfidence,
    derivedSectionStatuses,
    sectionCompletionPercentages,
  }), [
    collectedData,
    setCollectedDataPersisted,
    recommendationResult,
    setRecommendationResultPersisted,
    selectedOptionId,
    setSelectedOptionIdPersisted,
    activeDocumentId,
    setActiveDocumentIdPersisted,
    calculateConfidence,
    derivedSectionStatuses,
    sectionCompletionPercentages,
  ]);

  return (
    <RecommendationContext.Provider value={contextValue}>
      {children}
    </RecommendationContext.Provider>
  );
};
